/**
 * Reading-time estimates for the long-form content types (fundamentals,
 * glossary terms, comparisons, guides, tools, builds). Shared by the page
 * renderers AND the server's page-data builder (M6): index pages list dozens
 * of entities whose prose the browser no longer receives, so the server
 * computes each card's estimate with these SAME functions before stripping the
 * prose and ships it as `estimatedReadingMinutes`. The numbers therefore match
 * the detail page exactly, without shipping the text. Client-safe (type-only
 * content imports).
 */
import { blockText, estimateReadingMinutes } from './reading-time'
import type {
  Build,
  Comparison,
  Fundamental,
  GlossaryTerm,
  Guide,
  Tool,
} from './catalog'
import type { CollectionKey } from './catalog-api'

/** A snapshot entity whose reading time was computed on the server. */
export type ReadingTimed = { estimatedReadingMinutes?: number }

export function fundamentalReadingMinutes(fundamental: Fundamental): number {
  const precomputed = (fundamental as ReadingTimed).estimatedReadingMinutes
  if (typeof precomputed === 'number') return precomputed
  return estimateReadingMinutes([
    fundamental.intro,
    ...fundamental.sections.flatMap((section) => [
      section.body,
      ...(section.blocks?.flatMap(blockText) ?? []),
    ]),
    ...fundamental.faq.flatMap((item) => [item.question, item.answer]),
  ])
}

export function glossaryReadingMinutes(term: GlossaryTerm): number {
  const precomputed = (term as ReadingTimed).estimatedReadingMinutes
  if (typeof precomputed === 'number') return precomputed
  return estimateReadingMinutes([
    term.intro,
    ...term.sections.flatMap((section) => [
      section.body,
      ...(section.blocks?.flatMap(blockText) ?? []),
    ]),
    ...term.faq.flatMap((item) => [item.question, item.answer]),
  ])
}

export function comparisonReadingMinutes(comparison: Comparison): number {
  const precomputed = (comparison as ReadingTimed).estimatedReadingMinutes
  if (typeof precomputed === 'number') return precomputed
  return estimateReadingMinutes([
    comparison.intro,
    comparison.verdict,
    ...comparison.options.flatMap((option) => [
      option.tagline,
      option.bestFor,
      ...option.pros,
      ...option.cons,
    ]),
    ...comparison.faq.flatMap((item) => [item.question, item.answer]),
  ])
}

export function guideReadingMinutes(guide: Guide): number {
  const precomputed = (guide as ReadingTimed).estimatedReadingMinutes
  if (typeof precomputed === 'number') return precomputed
  return estimateReadingMinutes([
    guide.intro,
    ...guide.sections.flatMap((section) => [
      section.body,
      ...(section.blocks?.flatMap(blockText) ?? []),
    ]),
    ...(guide.howToSteps?.flatMap((step) => [step.name, step.text]) ?? []),
    ...guide.faq.flatMap((item) => [item.question, item.answer]),
  ])
}

export function toolReadingMinutes(tool: Tool): number {
  const precomputed = (tool as ReadingTimed).estimatedReadingMinutes
  if (typeof precomputed === 'number') return precomputed
  return estimateReadingMinutes([
    tool.intro,
    ...tool.sections.flatMap((section) => [
      section.body,
      ...(section.blocks?.flatMap(blockText) ?? []),
    ]),
    ...tool.faq.flatMap((item) => [item.question, item.answer]),
  ])
}

export function buildReadingMinutes(build: Build): number {
  const precomputed = (build as ReadingTimed).estimatedReadingMinutes
  if (typeof precomputed === 'number') return precomputed
  return estimateReadingMinutes([
    build.tagline,
    build.description,
    ...build.sections.flatMap((section) => [
      section.body,
      ...(section.blocks?.flatMap(blockText) ?? []),
    ]),
    ...build.lessonsLearned,
  ])
}

/**
 * Reading-time estimator per catalog collection, for the collections whose
 * index pages display an estimate. Used by `page-data.ts`.
 */
export const READING_MINUTES_BY_COLLECTION: Partial<
  Record<CollectionKey, (entity: never) => number>
> = {
  fundamentals: fundamentalReadingMinutes,
  glossaryTerms: glossaryReadingMinutes,
  comparisons: comparisonReadingMinutes,
  guides: guideReadingMinutes,
  tools: toolReadingMinutes,
  builds: buildReadingMinutes,
}
